<template>
    <b-table-simple sticky-header hover small caption-top striped class="entities-table">
      <colgroup>
        <col span="1" style="width: 30%;">
        <col span="1">
        <col span="1">
        <col span="1">
      </colgroup>
      <b-thead>
        <b-tr>
          <b-th class="pl-3 shadow-sm border-bottom-0">Parameter</b-th>
          <b-th class="text-right shadow-sm border-bottom-0">Value</b-th>
          <b-th class="shadow-sm border-bottom-0">Unit</b-th>
          <b-th class="shadow-sm border-bottom-0">Last change</b-th>
          <b-th class="shadow-sm border-bottom-0 text-center d-none d-xl-table-cell">Actions</b-th>
        </b-tr>
      </b-thead>
      <b-tbody>
        <b-tr v-for="parameter in parameters_filtered" :key="parameter.id" :variant="parameter_selected(parameter.id)">
          <b-td class="pl-3"><span v-b-popover.hover.right="'Entity: ' + entityId + ', Parameter: ' + parameter.id">{{ parameter.label }}</span></b-td>
          <b-td class="text-right" :class="parameter_muted_class(parameter_lastupdate(parameter.id))">
            <span v-if="parameter_valid(parameter.id)">{{ parameter_value(parameter.id) }}</span>
            <span v-else>n/a</span>
          </b-td>
          <b-td :class="parameter_muted_class(parameter_lastupdate(parameter.id))">{{ parameter.unit }}</b-td>
          <b-td :class="parameter_muted_class(parameter_lastupdate(parameter.id))" class="pr-3" :ref="'last_update_' + entityId + '_' + parameter.id" v-b-popover.hover.left="parameter_lastupdate_date(parameter.id)" :data-timestamp="parameter_lastupdate(parameter.id)">- No data -</b-td>
          <b-td class="text-center d-none d-xl-table-cell">
            <b-button :disabled="isNaN(parameter_value(parameter.id))" variant="primary" class="btn-xs mr-1" title="Show parameter in chart and on track" @click="selectParameter(parameter_ids(parameter.id))">
              Chart
            </b-button>
            <!--<b-button :disabled="isNaN(parameter_value(parameter.id)) || selectedParameter === null || parametersEqual(parameter_ids(parameter.id), selectedParameter) || parametersEqual(parameter_ids(parameter.id), selectedParameterCompare)"
                      variant="info" class="btn-xs" title="Compare parameter in chart with first parameter" @click="selectParameterCompare(parameter_ids(parameter.id))">
              Compare
            </b-button>&nbsp;-->
          </b-td>
        </b-tr>
      </b-tbody>
    </b-table-simple>
</template>

<script>
import moment from 'moment'
import { mapGetters, mapMutations, mapState } from 'vuex'
import Helpers from '@/mixins/helpers'

const UPDATE_INTERVAL_MS = 1000
const VALUE_MUTED_TIMEOUT = 3600

export default {
  name: 'GenericDataWidget',
  props: [
    'platformId',
    'entityId'
  ],
  mixins: [
    Helpers
  ],
  methods: {
    ...mapMutations(['select_parameter', 'select_parameter_compare']),
    getAge: function (timestamp, skip) {
      if (timestamp > 0) {
        return moment.unix(timestamp).fromNow(skip)
      } else {
        return 'never'
      }
    },
    selectParameter: function (parameter) {
      this.select_parameter(parameter)
      this.select_parameter_compare(null)
    },
    selectParameterCompare: function (parameter) {
      this.select_parameter_compare(parameter)
    },
    parametersEqual: function (a, b) {
      return Array.isArray(a) && Array.isArray(b) && a[0] === b[0] && a[1] === b[1] && a[2] === b[2]
    },
    updateLastUpdate: function () {
      for (const ref in this.$refs) {
        const timestamp = Number(this.$refs[ref][0].$attrs['data-timestamp'])
        if (timestamp > 0) {
          this.$refs[ref][0].$el.innerText = this.getAge(timestamp, false)
        }
      }
    }
  },
  created: function () {
    // Configure moment
    moment.relativeTimeThreshold('ss', -1)
  },
  mounted: function () {
    // Initialize last change timestamp
    this.updateLastUpdate()
    // Start last change timestamp updater
    this.updateTask = setInterval(this.updateLastUpdate, UPDATE_INTERVAL_MS)
  },
  beforeDestroy: function () {
    // Remove background job again
    clearInterval(this.updateTask)
  },
  computed: {
    ...mapState(['selectedParameter', 'selectedParameterCompare']),
    ...mapGetters(['parameters', 'parameter', 'currentUpdate']),
    parameters_filtered: function () {
      const parameters = this.parameters(this.platformId, this.entityId)
      const filteredParameters = {}
      for (const parameterId in parameters) {
        if (Object.prototype.hasOwnProperty.call(parameters, parameterId)) {
          const parameter = parameters[parameterId]
          if (Number(parameter.restricted) === 0 || 'restricted' in this.$route.query) {
            filteredParameters[parameterId] = parameter
          }
        }
      }
      return filteredParameters
    },
    parameter_selected: function () {
      return (parameterId) => {
        // Compare every element as comparing of arrays is difficult
        if (this.selectedParameter !== null) {
          if (this.selectedParameter[0] === this.platformId &&
            this.selectedParameter[1] === this.entityId &&
            this.selectedParameter[2] === parameterId) {
            return 'primary'
          }
          if (this.selectedParameterCompare !== null) {
            if (this.selectedParameterCompare[0] === this.platformId &&
              this.selectedParameterCompare[1] === this.entityId &&
              this.selectedParameterCompare[2] === parameterId) {
              return 'info'
            }
          }
        }
        return 'default'
      }
    },
    parameter_ids: function () {
      return (parameterId) => {
        return [Number(this.platformId), Number(this.entityId), Number(parameterId)]
      }
    },
    parameter_value: function () {
      return (parameterId) => {
        // Note: Field precision was renamed due to MariaDB field restrictions (precision is reserved keyword)
        const parameter = this.parameter(this.platformId, this.entityId, parameterId)
        if (parameter.current.length !== 0) {
          if (Number(parameter.num_precision) > 0 && parameter.type === 'float') {
            return this.showDigits(this.roundDigits(parameter.current[1],
              parameter.num_precision), parameter.num_precision)
          }
          return parameter.current[1]
        }
        return 'n/a'
      }
    },
    parameter_lastupdate: function () {
      return (parameterId) => {
        const parameter = this.parameter(this.platformId, this.entityId, parameterId)
        if (parameter.current.length !== 0) {
          return Number(parameter.current[0] / 1000)
        }
        return -1
      }
    },
    parameter_valid: function () {
      return (parameterId) => {
        const lastUpdate = this.parameter_lastupdate(parameterId)
        const parameter = this.parameter(this.platformId, this.entityId, parameterId)
        return lastUpdate > 0 && (parameter.timeout === 0 || (parameter.timeout > 0 && (Date.now() / 1000) - lastUpdate < parameter.timeout))
      }
    },
    parameter_muted_class: function () {
      return (timestamp) => {
        return ((Date.now() / 1000) - timestamp > VALUE_MUTED_TIMEOUT) ? 'text-muted' : ''
      }
    },
    parameter_lastupdate_date: function () {
      return (parameterId) => {
        const lastupdate = this.parameter_lastupdate(parameterId)
        if (lastupdate > 0) {
          return moment.unix(this.parameter_lastupdate(parameterId)).format('MMMM DD YYYY HH:mm')
        }
        return 'No data available'
      }
    }
  }
}

</script>

<style scoped>

.entities-table {
  margin-bottom: 0;
  max-height: 600px;
  width: 100%;
}

tr > th {
  border-top: 0;
}

</style>
